import { ReactNode } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { CheckIcon, Icon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { ActiveIndicator } from '@/components/ActionBar/ActionBar.types';
import { DropdownMenuItemWithPopover } from './DropdownMenuItemWithPopover';

interface DropdownMenuItemProps {
    icon?: Icon;
    label: string;
    disabled?: boolean;
    isActive?: boolean;
    activeIndicator?: ActiveIndicator;
    onClick?: () => void;
    children?: ReactNode;
    alignOffset?: number;
    trailingBadge?: ReactNode;
    testId?: string;
}

export function DropdownMenuItem(props: DropdownMenuItemProps) {
    const {
        icon: IconComponent,
        label,
        disabled = false,
        isActive,
        activeIndicator = ActiveIndicator.CHECK,
        children,
        onClick,
        alignOffset = 0,
        trailingBadge,
        testId,
    } = props;

    if (children) {
        return (
            <DropdownMenuItemWithPopover
                icon={IconComponent}
                label={label}
                isActive={isActive}
                activeIndicator={activeIndicator}
                alignOffset={alignOffset}
                disabled={disabled}
                trailingBadge={trailingBadge}
                testId={testId}
            >
                {children}
            </DropdownMenuItemWithPopover>
        );
    }

    const item = (
        <DropdownMenuPrimitive.Item
            data-testid={testId}
            disabled={disabled}
            onClick={onClick}
            className="flex items-center gap-1.5 py-1.5 pl-2 pr-1.5 min-h-8 rounded-[5px] outline-none cursor-pointer transition-colors hover:bg-surface-hover-strong focus:bg-surface-hover-strong data-[disabled]:text-grey-300 group"
        >
            {IconComponent && (
                <span className="relative inline-flex size-4 flex-shrink-0 items-center justify-center">
                    <IconComponent
                        className={cn(
                            'size-4',
                            isActive
                                ? 'text-white'
                                : 'text-grey-200 group-hover:text-white-100 group-data-[disabled]:text-grey-300',
                        )}
                    />
                    {isActive && activeIndicator === ActiveIndicator.DOT && (
                        <span
                            className="pointer-events-none absolute bottom-0 right-0 w-[3px] h-[3px] rounded-full bg-white-100"
                            aria-hidden="true"
                        />
                    )}
                </span>
            )}

            <span
                className={cn(
                    'flex-1 min-w-0 text-[14px] leading-5 font-normal',
                    isActive
                        ? 'text-white'
                        : 'text-white group-data-[disabled]:text-grey-300',
                )}
            >
                {label}
            </span>

            {isActive && activeIndicator === ActiveIndicator.CHECK && (
                <CheckIcon className="size-4 text-white flex-shrink-0 group-data-[disabled]:text-grey-300" />
            )}
        </DropdownMenuPrimitive.Item>
    );

    return item;
}
